import React, { useState } from 'react';
import { ChevronRight, Sparkles } from 'lucide-react';
import { formatCurrency } from './formatters';

/**
 * Report detail drawer — the parts every "what's behind this number" drawer
 * (P&L, Revenue) is built from, top to bottom:
 *
 *   DetailAnswer   the number, how far it is from budget in words, one plain
 *                  sentence of why, and a strip of comparisons (DetailStats);
 *                  `action` sits at the end of the number's line
 *   DetailSection  a titled block: "Why", then the detail behind it
 *   DivergingBars  what moved it — under budget left, over budget right
 *   SplitTwoWays   a variance split into its two causes (volume vs rate)
 *   ColumnStrip    a small column chart (variance by month, rooms by day)
 *
 * Over / under is always said in words, never as a signed number; the tone
 * says whether that is good or bad. Costs run "over" / "under" budget;
 * revenue and profit come in "above" / "below" it ("over budget" reads as
 * overspending, which makes no sense on a revenue line).
 */

const money0 = (v, currency) => formatCurrency(Math.abs(Number(v) || 0), { currency, decimals: 0 });

/**
 * Variance → words + tone. `goodWhen`: 'over' for revenue and profit lines,
 * 'under' for costs. `dir` is 'over' / 'under' / 'on' for logic; `word` is
 * what to say: 'above' / 'below' for revenue and profit, 'over' / 'under'
 * for costs. Within half a unit reads "on budget".
 */
export function overUnder(variance, { goodWhen = 'over', currency, suffix = 'budget' } = {}) {
  const v = Number(variance);
  if (variance === null || variance === undefined || !Number.isFinite(v)) return null;
  if (Math.abs(v) < 0.5) return { dir: 'on', word: 'on', text: `On ${suffix}`, short: `on ${suffix}`, tone: 'flat' };
  const dir = v > 0 ? 'over' : 'under';
  const word = aboveBelow(dir, goodWhen);
  return {
    dir,
    word,
    text: `${money0(v, currency)} ${word} ${suffix}`,
    short: `${money0(v, currency)} ${word}`,
    tone: dir === goodWhen ? 'good' : 'bad',
  };
}

/** 'over' / 'under' → the word for the line: above / below unless it is a cost. */
export function aboveBelow(dir, goodWhen = 'over') {
  if (goodWhen === 'under') return dir;
  return dir === 'over' ? 'above' : dir === 'under' ? 'below' : dir;
}

/** "11.1%" of a variance against its base (null when the base is 0). */
export function variancePct(variance, base) {
  const b = Math.abs(Number(base) || 0);
  if (!b || variance === null || variance === undefined) return null;
  return `${(Math.abs(Number(variance)) / b * 100).toFixed(1)}%`;
}

export function DetailPill({ tone = 'flat', children }) {
  return <span className={`rn-dd-pill rn-dd-${tone}`}>{children}</span>;
}

/** stats: [{ label, value, sub?, subTone?, loading? }] */
export function DetailStats({ stats = [] }) {
  if (!stats.length) return null;
  return (
    <dl className="rn-dd-stats" style={{ gridTemplateColumns: `repeat(${stats.length}, minmax(0, 1fr))` }}>
      {stats.map((s) => (
        <div key={s.label}>
          <dt>{s.label}</dt>
          <dd>{s.loading ? <span className="lodge-skeleton rn-dd-skel" role="status" aria-label="Loading" /> : s.value ?? '—'}</dd>
          {s.sub ? <span className={s.subTone ? `rn-dd-${s.subTone}-ink` : undefined}>{s.sub}</span> : null}
        </div>
      ))}
    </dl>
  );
}

export function DetailAnswer({ value, pill, sentence, stats = [], action }) {
  return (
    <section className="rn-dd-answer" aria-label="Answer">
      <div className="rn-dd-answer-top">
        <b className="rn-dd-amount">{value}</b>
        {pill ? <DetailPill tone={pill.tone}>{pill.text}</DetailPill> : null}
        {action ? <span className="rn-dd-answer-action">{action}</span> : null}
      </div>
      {sentence ? <p className="rn-dd-sentence">{sentence}</p> : null}
      <DetailStats stats={stats} />
    </section>
  );
}

export function DetailSection({ title, aside, children, note }) {
  return (
    <section className="rn-dd-section">
      <header>
        <h4>{title}</h4>
        {aside ? <span>{aside}</span> : null}
      </header>
      {children}
      {note ? <p className="rn-dd-note">{note}</p> : null}
    </section>
  );
}

/**
 * rows: [{ id, label, sub?, amount (signed: + over, − under), text, tone, onClick? }]
 * total: { label, text, tone } (optional footer row)
 * The legend sits on the rows' own grid, so its split lines up with the
 * bars' zero line; a row that opens something ends in a chevron.
 */
export function DivergingBars({ rows = [], total, legend = ['Under budget', 'Over budget'] }) {
  const max = rows.reduce((m, r) => Math.max(m, Math.abs(Number(r.amount) || 0)), 0) || 1;
  return (
    <div className="rn-dd-div">
      <div className="rn-dd-div-row rn-dd-div-legend" aria-hidden="true">
        <span />
        <span className="rn-dd-div-legend-split">
          <span>{legend[0]}</span>
          <span>{legend[1]}</span>
        </span>
      </div>
      {rows.map((r) => {
        const w = Math.max(1.5, (Math.abs(Number(r.amount) || 0) / max) * 50);
        const right = Number(r.amount) >= 0;
        const Tag = r.onClick ? 'button' : 'div';
        return (
          <Tag
            key={r.id}
            type={r.onClick ? 'button' : undefined}
            className={`rn-dd-div-row${r.onClick ? ' rn-dd-hit' : ''}`}
            onClick={r.onClick}
            title={r.onClick ? `Open ${r.label}` : undefined}
          >
            <span className="rn-dd-div-label">
              <span>{r.label}</span>
              {r.sub ? <small>{r.sub}</small> : null}
            </span>
            <span className="rn-dd-div-track" aria-hidden="true">
              <i className={`rn-dd-${r.tone}-bg`} style={right ? { left: '50%', width: `${w}%` } : { right: '50%', width: `${w}%` }} />
            </span>
            <span className={`rn-dd-div-amt rn-dd-${r.tone}-ink`}>{r.text}</span>
            {r.onClick ? <ChevronRight size={14} className="rn-dd-div-go" aria-hidden="true" /> : <span />}
          </Tag>
        );
      })}
      {total ? (
        <div className="rn-dd-div-row rn-dd-div-total">
          <span className="rn-dd-div-label"><span>{total.label}</span></span>
          <span />
          <span className={`rn-dd-div-amt rn-dd-${total.tone}-ink`}>{total.text}</span>
          <span />
        </div>
      ) : null}
    </div>
  );
}

/** parts: [{ key, label, text (amount in words), tone, detail }] */
export function SplitTwoWays({ parts = [] }) {
  return (
    <div className="rn-dd-split">
      {parts.map((p) => (
        <div key={p.key} className="rn-dd-split-part">
          <div className="rn-dd-split-head">
            <span className={`rn-dd-dot rn-dd-${p.tone}-bg`} aria-hidden="true" />
            <b>{p.label}</b>
            <span className={`rn-dd-${p.tone}-ink`}>{p.text}</span>
          </div>
          {p.detail ? <p>{p.detail}</p> : null}
        </div>
      ))}
    </div>
  );
}

/**
 * Small column chart. points: [{ key, label, value, tone, tick? (a marker
 * value on the same scale, e.g. the budget), title?, active? }]
 * `diverging`: columns grow up for + and down for − from a mid baseline.
 * `readout`: a line under the chart says the hovered or focused column's
 * title (the active one, else the latest, otherwise), so the values read
 * without a tooltip — on touch and from the keyboard too.
 */
export function ColumnStrip({ points = [], diverging = false, height = 72, onPick, readout = false }) {
  const [pointed, setPointed] = useState(null);
  const max = points.reduce((m, p) => Math.max(m, Math.abs(Number(p.value) || 0), Math.abs(Number(p.tick) || 0)), 0) || 1;
  const shown = readout ? points.find((p) => p.key === pointed) || points.find((p) => p.active) || points[points.length - 1] : null;
  const strip = (
    <div className={`rn-dd-cols${diverging ? ' rn-dd-cols-div' : ''}`} style={{ height }} onMouseLeave={readout ? () => setPointed(null) : undefined}>
      {points.map((p) => {
        const v = Number(p.value) || 0;
        const h = (Math.abs(v) / max) * (diverging ? 50 : 100);
        const bar = diverging
          ? (v >= 0 ? { bottom: '50%', height: `${h}%` } : { top: '50%', height: `${h}%` })
          : { bottom: 0, height: `${h}%` };
        const Tag = onPick ? 'button' : 'div';
        return (
          <Tag
            key={p.key}
            type={onPick ? 'button' : undefined}
            className={`rn-dd-col${p.active ? ' rn-dd-col-active' : ''}${onPick ? ' rn-dd-hit' : ''}${shown && p.key === shown.key ? ' rn-dd-col-shown' : ''}`}
            title={readout ? undefined : p.title}
            aria-label={readout ? p.title : undefined}
            onClick={onPick ? () => onPick(p) : undefined}
            onMouseEnter={readout ? () => setPointed(p.key) : undefined}
            onFocus={readout ? () => setPointed(p.key) : undefined}
            onBlur={readout ? () => setPointed(null) : undefined}
          >
            <span className="rn-dd-col-plot">
              <i className={`rn-dd-${p.tone || 'flat'}-bg`} style={bar} />
              {p.tick !== undefined && p.tick !== null ? (
                <b className="rn-dd-col-tick" style={{ bottom: `${(Math.abs(Number(p.tick)) / max) * 100}%` }} />
              ) : null}
            </span>
            <span className="rn-dd-col-label">{p.label}</span>
          </Tag>
        );
      })}
    </div>
  );
  if (!readout) return strip;
  return (
    <>
      {strip}
      <p className="rn-dd-cols-readout" aria-live="polite">{shown?.title || ' '}</p>
    </>
  );
}

/** Open the assistant and run `prompt` scoped to a property. */
export function askLodgeicAi({ prompt, hotelId, hotelName, label }) {
  window.dispatchEvent(new CustomEvent('lodgeic:assistant:open', {
    detail: {
      expanded: false,
      ...(hotelId ? { hotelId, hotelName } : {}),
      action: { prompt, label: label || 'Explain this number' },
    },
  }));
}

export function AskAiButton({ prompt, hotelId, hotelName }) {
  return (
    <button type="button" className="rn-drawer-btn" onClick={() => askLodgeicAi({ prompt, hotelId, hotelName })}>
      <Sparkles size={13} aria-hidden="true" /> Ask Lodgeic AI
    </button>
  );
}

/** An Export sheet: [label, value] pairs and tables in one array of arrays. */
export function sheetFromSections(sections) {
  const aoa = [];
  sections.filter(Boolean).forEach((s, i) => {
    if (i) aoa.push([]);
    if (s.title) aoa.push([s.title]);
    if (s.head) aoa.push(s.head);
    (s.rows || []).forEach((r) => aoa.push(r));
  });
  return aoa;
}
